import { Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms'
import { DateInputComponent } from '../date-input/date-input.component'
import { TimeInputComponent } from '../time-input/time-input.component'
import { DateTime } from '../../models/DateTime/DateTime'
import { DateSegment } from '../../models/DateTime/DateSegment'
import { TimeSegment } from '../../models/DateTime/TimeSegment'

@Component({
  selector: 'app-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  host: {
    '[class.disabled]': 'disabled',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
  ],
  imports: [DateInputComponent, TimeInputComponent],
})
export class DateTimeInputComponent implements ControlValueAccessor {
  @Input() value: DateTime = new DateTime(null, null)
  @Input() disabled: boolean = false
  @Input() postponeChangeUntilBlur: boolean = false

  @Output() changed = new EventEmitter<DateTime>()
  @Output() dateChanged = new EventEmitter<DateSegment>()
  @Output() timeChanged = new EventEmitter<TimeSegment>()

  @ViewChild('dateInputComponent', { static: false }) dateComponent: DateInputComponent
  @ViewChild('timeInputComponent', { static: false }) timeComponent: TimeInputComponent

  private onChange = (value: DateTime) => {}
  protected onTouched: (_: any) => void = () => {}

  constructor() {}

  writeValue(value: DateTime | string | Date | null | undefined) {
    if (!value) {
      this.value = new DateTime(null, null)
    } else if (typeof value === 'string') {
      // ISO string → Date → DateSegment + TimeSegment → DateTime
      const dateObj = new Date(value)
      this.value = new DateTime(
        DateSegment.fromDate(dateObj),
        new TimeSegment(dateObj.getHours(), dateObj.getMinutes()),
      )
    } else if (value instanceof Date) {
      this.value = new DateTime(DateSegment.fromDate(value), new TimeSegment(value.getHours(), value.getMinutes()))
    } else {
      this.value = value
    }

    if (this.dateComponent) this.dateComponent.value = this.value.date
    if (this.timeComponent) this.timeComponent.value = this.value.time
  }

  registerOnChange(fn: (value: DateTime) => void) {
    this.onChange = fn
  }

  registerOnTouched(fn: (_: any) => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled
    if (this.dateComponent) {
      this.dateComponent.disabled = isDisabled
    }
    if (this.timeComponent) {
      this.timeComponent.disabled = isDisabled
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return null
  }

  handleDateChange() {
    const dateSegment = this.dateComponent.value
    const timeSegment = this.value?.time ?? null
    this.value = new DateTime(dateSegment, timeSegment)

    this.onChange(this.value)
    this.changed.emit(this.value)
    this.dateChanged.emit(dateSegment)
  }

  handleTimeChange() {
    const timeSegment = this.timeComponent.value
    const dateSegment = this.value?.date ?? null
    this.value = new DateTime(dateSegment, timeSegment)

    this.onChange(this.value)
    this.changed.emit(this.value)
    this.timeChanged.emit(timeSegment)
  }
}
